import React, { useState, useEffect, useRef } from 'react';
import axios from '../../config/axiosConfig';
import API_BASE_URL from '../../config/api';
import { 
    MdArrowBack, 
    MdPublic, 
    MdArticle, 
    MdRefresh,
    MdFilterList,
    MdDownload,
    MdTrendingUp,
    MdTrendingDown,
    MdSchedule,
    MdClose,
    MdFlag,
    MdLocationCity,
    MdSmartphone,
    MdComputer,
    MdTabletMac,
    MdShare
} from 'react-icons/md';
import { 
    FaGoogle, 
    FaWhatsapp, 
    FaFacebook, 
    FaInstagram, 
    FaYoutube, 
    FaTelegram, 
    FaTwitter, 
    FaCompass 
} from 'react-icons/fa';

const DetailedVisitorAnalytics = ({ onBack }) => {
    // 1. Date Range State
    const [period, setPeriod] = useState('30days');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [showCustomModal, setShowCustomModal] = useState(false);

    // 2. Granularity State (Hourly, Daily, Weekly, Monthly)
    const [granularity, setGranularity] = useState('daily');

    // 3. Chart Series Visibility Toggles
    const [visibleSeries, setVisibleSeries] = useState({
        unique_visitors: true,
        sessions: true,
        page_views: true,
    });

    // 4. Multi-Faceted Filters
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedState, setSelectedState] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [selectedDevice, setSelectedDevice] = useState('');
    const [selectedOS, setSelectedOS] = useState('');
    const [selectedBrowser, setSelectedBrowser] = useState('');
    const [selectedSource, setSelectedSource] = useState('');

    // 5. Active Sub-View Tab
    const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'geo' | 'content' | 'sources' | 'tech' | 'realtime'

    // 6. Content Sorting & Trending State
    const [contentSort, setContentSort] = useState('views'); // 'views' | 'visitors' | 'avg_time'
    const [trendingPeriod, setTrendingPeriod] = useState('last_24h'); // 'last_1h' | 'last_6h' | 'last_24h'
    const [pageSearch, setPageSearch] = useState('');

    // 7. Country & City Search & Pagination
    const [countrySearch, setCountrySearch] = useState('');
    const [countryPage, setCountryPage] = useState(1);
    const [citySearch, setCitySearch] = useState('');
    const [cityPage, setCityPage] = useState(1);

    // Data, Loading & Error State
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastRefreshed, setLastRefreshed] = useState(new Date());

    // Export Dropdown State
    const [showExportMenu, setShowExportMenu] = useState(false);
    const printAreaRef = useRef(null);

    // Fetch Stats with active query parameters
    const fetchAnalytics = async (isSilent = false) => {
        if (!isSilent) setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            params.append('period', period);
            params.append('granularity', granularity);
            if (period === 'custom' && customStartDate && customEndDate) {
                params.append('start_date', customStartDate);
                params.append('end_date', customEndDate);
            }
            if (selectedCountry) params.append('country', selectedCountry);
            if (selectedState) params.append('state', selectedState);
            if (selectedCity) params.append('city', selectedCity);
            if (selectedDevice) params.append('device', selectedDevice);
            if (selectedOS) params.append('os', selectedOS);
            if (selectedBrowser) params.append('browser', selectedBrowser);
            if (selectedSource) params.append('source', selectedSource);
            if (contentSort) params.append('sort_by', contentSort);

            const res = await axios.get(`analytics/detailed-stats/?${params.toString()}`);
            setData(res.data);
            setLastRefreshed(new Date());
        } catch (err) {
            console.error("Error loading visitor analytics:", err);
            setError("Failed to load visitor statistics. Please try again.");
        } finally {
            if (!isSilent) setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, [period, granularity, selectedCountry, selectedState, selectedCity, selectedDevice, selectedOS, selectedBrowser, selectedSource, contentSort]);

    // Auto-refresh real-time data silently every 30 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            fetchAnalytics(true);
        }, 30000);
        return () => clearInterval(interval);
    }, [period, granularity, selectedCountry, selectedState, selectedCity, selectedDevice, selectedOS, selectedBrowser, selectedSource]);

    // Format numbers
    const fmt = (num) => (num || 0).toLocaleString();

    // Chart scaling
    const chartPoints = data?.chart_series || [];
    const maxChartVal = chartPoints.reduce((max, pt) => {
        let val = 0;
        if (visibleSeries.unique_visitors) val = Math.max(val, pt.unique_visitors || 0);
        if (visibleSeries.sessions) val = Math.max(val, pt.sessions || 0);
        if (visibleSeries.page_views) val = Math.max(val, pt.page_views || 0);
        return Math.max(max, val);
    }, 10) || 10;

    // Handle CSV Export
    const handleCsvExport = () => {
        setShowExportMenu(false);
        const params = new URLSearchParams();
        params.append('period', period);
        if (period === 'custom' && customStartDate && customEndDate) {
            params.append('start_date', customStartDate);
            params.append('end_date', customEndDate);
        }
        if (selectedCountry) params.append('country', selectedCountry);
        if (selectedState) params.append('state', selectedState);
        if (selectedCity) params.append('city', selectedCity);
        if (selectedDevice) params.append('device', selectedDevice);
        if (selectedOS) params.append('os', selectedOS);
        if (selectedBrowser) params.append('browser', selectedBrowser);
        if (selectedSource) params.append('source', selectedSource);

        window.open(`${API_BASE_URL}analytics/export/?${params.toString()}`, '_blank');
    };

    // Handle Print / PDF Export
    const handlePrintPdf = () => {
        setShowExportMenu(false);
        window.print();
    };

    // Filter and Pagination Helpers
    const filteredCountries = data?.countries?.filter(c => 
        c.country.toLowerCase().includes(countrySearch.toLowerCase())
    ) || [];
    const countryItemsPerPage = 10;
    const paginatedCountries = filteredCountries.slice((countryPage - 1) * countryItemsPerPage, countryPage * countryItemsPerPage);

    const filteredCities = data?.cities?.filter(c => 
        c.city.toLowerCase().includes(citySearch.toLowerCase()) || 
        c.state.toLowerCase().includes(citySearch.toLowerCase())
    ) || [];
    const cityItemsPerPage = 10;
    const paginatedCities = filteredCities.slice((cityPage - 1) * cityItemsPerPage, cityPage * cityItemsPerPage);

    const filteredPages = data?.top_pages?.filter(p => 
        p.title.toLowerCase().includes(pageSearch.toLowerCase()) || 
        p.path.toLowerCase().includes(pageSearch.toLowerCase())
    ) || [];

    const getSourceIcon = (source) => {
        const s = (source || '').toLowerCase();
        if (s.includes('google')) return <FaGoogle className="text-red-500" />;
        if (s.includes('whatsapp')) return <FaWhatsapp className="text-green-500" />;
        if (s.includes('facebook')) return <FaFacebook className="text-blue-600" />;
        if (s.includes('instagram')) return <FaInstagram className="text-pink-600" />;
        if (s.includes('youtube')) return <FaYoutube className="text-red-600" />;
        if (s.includes('telegram')) return <FaTelegram className="text-sky-500" />;
        if (s.includes('twitter') || s.includes('x')) return <FaTwitter className="text-blue-400" />;
        if (s.includes('direct')) return <FaCompass className="text-indigo-500" />;
        return <MdShare className="text-gray-500" />;
    };

    // Check active filters count
    const activeFilterCount = [selectedCountry, selectedState, selectedCity, selectedDevice, selectedOS, selectedBrowser, selectedSource].filter(Boolean).length;

    const clearAllFilters = () => {
        setSelectedCountry('');
        setSelectedState('');
        setSelectedCity('');
        setSelectedDevice('');
        setSelectedOS('');
        setSelectedBrowser('');
        setSelectedSource('');
    };

    return (
        <div className="p-4 sm:p-6 bg-gray-50 min-h-screen text-gray-800" ref={printAreaRef}>
            {/* Top Navigation & Date Bar */}
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm mb-6 flex flex-col xl:flex-row xl:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onBack}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3.5 py-2 rounded-xl text-xs sm:text-sm font-semibold flex items-center gap-2 transition-colors shadow-sm"
                    >
                        <MdArrowBack className="w-4 h-4" /> Back
                    </button>
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-black text-gray-800 tracking-tight">Visitor Statistics</h1>
                            {/* Live Badge */}
                            <div className="hidden sm:inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold px-2.5 py-0.5 rounded-full">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                </span>
                                {data?.realtime?.currently_online || 0} Online
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Real-time traffic metrics, audience geography, campaigns, and content performance • Updated {lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </p>
                    </div>
                </div>

                {/* Date Controls & Action Buttons */}
                <div className="flex flex-wrap items-center gap-2">
                    {/* Date Range Selector */}
                    <div className="inline-flex bg-gray-100 p-1 rounded-xl text-xs font-semibold text-gray-700 overflow-x-auto max-w-full">
                        {[
                            { id: 'today', label: 'Today' },
                            { id: 'yesterday', label: 'Yesterday' },
                            { id: '7days', label: '7D' },
                            { id: '30days', label: '30D' },
                            { id: 'month', label: 'This Month' },
                            { id: 'last_month', label: 'Last Month' },
                            { id: 'year', label: 'This Year' },
                            { id: 'custom', label: 'Custom' },
                        ].map((btn) => (
                            <button
                                key={btn.id}
                                onClick={() => {
                                    if (btn.id === 'custom') {
                                        setShowCustomModal(true);
                                    } else {
                                        setPeriod(btn.id);
                                    }
                                }}
                                className={`px-2.5 py-1.5 rounded-lg transition-all whitespace-nowrap ${
                                    period === btn.id
                                        ? 'bg-white text-blue-600 shadow-sm font-bold'
                                        : 'hover:text-gray-900 text-gray-600'
                                }`}
                            >
                                {btn.label}
                            </button>
                        ))}
                    </div>

                    {/* Export Dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => setShowExportMenu(!showExportMenu)}
                            className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm transition-colors"
                        >
                            <MdDownload className="w-4 h-4 text-blue-600" /> Export
                        </button>
                        {showExportMenu && (
                            <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-gray-100 z-50 py-1 text-xs">
                                <button
                                    onClick={handleCsvExport}
                                    className="w-full text-left px-4 py-2.5 hover:bg-blue-50 text-gray-700 flex items-center gap-2 font-medium"
                                >
                                    📄 Export as CSV
                                </button>
                                <button
                                    onClick={handleCsvExport}
                                    className="w-full text-left px-4 py-2.5 hover:bg-blue-50 text-gray-700 flex items-center gap-2 font-medium"
                                >
                                    📊 Export as Excel (.csv)
                                </button>
                                <button
                                    onClick={handlePrintPdf}
                                    className="w-full text-left px-4 py-2.5 hover:bg-blue-50 text-gray-700 flex items-center gap-2 font-medium"
                                >
                                    📑 Print / Save as PDF
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Refresh Button */}
                    <button
                        onClick={() => fetchAnalytics()}
                        className="p-2 text-gray-600 hover:text-blue-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-sm transition-colors"
                        title="Refresh"
                    >
                        <MdRefresh className={`w-4 h-4 ${loading ? 'animate-spin text-blue-600' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Active Filters Tag Bar */}
            {activeFilterCount > 0 && (
                <div className="bg-blue-50/80 border border-blue-200 p-3 rounded-xl mb-6 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="font-bold text-blue-900 flex items-center gap-1">
                            <MdFilterList /> Active Filters ({activeFilterCount}):
                        </span>
                        {selectedCountry && (
                            <span className="bg-white text-blue-800 px-2.5 py-1 rounded-md border border-blue-200 flex items-center gap-1">
                                Country: <strong>{selectedCountry}</strong>
                                <button onClick={() => setSelectedCountry('')}><MdClose /></button>
                            </span>
                        )}
                        {selectedState && (
                            <span className="bg-white text-blue-800 px-2.5 py-1 rounded-md border border-blue-200 flex items-center gap-1">
                                State: <strong>{selectedState}</strong>
                                <button onClick={() => setSelectedState('')}><MdClose /></button>
                            </span>
                        )}
                        {selectedCity && (
                            <span className="bg-white text-blue-800 px-2.5 py-1 rounded-md border border-blue-200 flex items-center gap-1">
                                City: <strong>{selectedCity}</strong>
                                <button onClick={() => setSelectedCity('')}><MdClose /></button>
                            </span>
                        )}
                        {selectedDevice && (
                            <span className="bg-white text-blue-800 px-2.5 py-1 rounded-md border border-blue-200 flex items-center gap-1">
                                Device: <strong>{selectedDevice}</strong>
                                <button onClick={() => setSelectedDevice('')}><MdClose /></button>
                            </span>
                        )}
                        {selectedOS && (
                            <span className="bg-white text-blue-800 px-2.5 py-1 rounded-md border border-blue-200 flex items-center gap-1">
                                OS: <strong>{selectedOS}</strong>
                                <button onClick={() => setSelectedOS('')}><MdClose /></button>
                            </span>
                        )}
                        {selectedBrowser && (
                            <span className="bg-white text-blue-800 px-2.5 py-1 rounded-md border border-blue-200 flex items-center gap-1">
                                Browser: <strong>{selectedBrowser}</strong>
                                <button onClick={() => setSelectedBrowser('')}><MdClose /></button>
                            </span>
                        )}
                        {selectedSource && (
                            <span className="bg-white text-blue-800 px-2.5 py-1 rounded-md border border-blue-200 flex items-center gap-1">
                                Source: <strong>{selectedSource}</strong>
                                <button onClick={() => setSelectedSource('')}><MdClose /></button>
                            </span>
                        )}
                    </div>
                    <button
                        onClick={clearAllFilters}
                        className="text-xs font-bold text-red-600 hover:text-red-800 underline"
                    >
                        Clear All Filters
                    </button>
                </div>
            )}

            {/* Custom Date Range Modal */}
            {showCustomModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-gray-100">
                        <h3 className="font-bold text-gray-800 text-base mb-4">Select Custom Date Range</h3>
                        <div className="space-y-3 text-xs">
                            <div>
                                <label className="block text-gray-600 font-semibold mb-1">Start Date</label>
                                <input
                                    type="date"
                                    value={customStartDate}
                                    onChange={(e) => setCustomStartDate(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-gray-600 font-semibold mb-1">End Date</label>
                                <input
                                    type="date"
                                    value={customEndDate}
                                    onChange={(e) => setCustomEndDate(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg text-xs"
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 mt-5">
                            <button
                                onClick={() => setShowCustomModal(false)}
                                className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    if (customStartDate && customEndDate) {
                                        setPeriod('custom');
                                        setShowCustomModal(false);
                                    }
                                }}
                                disabled={!customStartDate || !customEndDate}
                                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold text-xs px-4 py-2 rounded-lg"
                            >
                                Apply Range
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs flex items-center justify-between">
                    <span>{error}</span>
                    <button onClick={() => fetchAnalytics()} className="underline font-bold">Retry</button>
                </div>
            )}

            {/* 2. Main KPI Cards (9 Cards) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-3 mb-8">
                {/* Total Visitors */}
                <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <span className="text-[11px] font-semibold text-gray-400 block truncate">Total Visitors</span>
                    <div className="text-xl font-black text-gray-800 mt-1">
                        {fmt(data?.kpis?.total_visitors?.value)}
                    </div>
                    <div className="mt-1.5 flex items-center text-[10px] font-bold">
                        {(data?.kpis?.total_visitors?.change_pct || 0) >= 0 ? (
                            <span className="text-emerald-600 flex items-center">
                                <MdTrendingUp className="mr-0.5" /> +{data?.kpis?.total_visitors?.change_pct}%
                            </span>
                        ) : (
                            <span className="text-rose-600 flex items-center">
                                <MdTrendingDown className="mr-0.5" /> {data?.kpis?.total_visitors?.change_pct}%
                            </span>
                        )}
                    </div>
                </div>

                {/* Unique Visitors */}
                <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <span className="text-[11px] font-semibold text-gray-400 block truncate">Unique Visitors</span>
                    <div className="text-xl font-black text-blue-600 mt-1">
                        {fmt(data?.kpis?.unique_visitors?.value)}
                    </div>
                    <div className="mt-1.5 flex items-center text-[10px] font-bold">
                        {(data?.kpis?.unique_visitors?.change_pct || 0) >= 0 ? (
                            <span className="text-emerald-600 flex items-center">
                                <MdTrendingUp className="mr-0.5" /> +{data?.kpis?.unique_visitors?.change_pct}%
                            </span>
                        ) : (
                            <span className="text-rose-600 flex items-center">
                                <MdTrendingDown className="mr-0.5" /> {data?.kpis?.unique_visitors?.change_pct}%
                            </span>
                        )}
                    </div>
                </div>

                {/* Sessions */}
                <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <span className="text-[11px] font-semibold text-gray-400 block truncate">Sessions</span>
                    <div className="text-xl font-black text-indigo-600 mt-1">
                        {fmt(data?.kpis?.sessions?.value)}
                    </div>
                    <div className="mt-1.5 flex items-center text-[10px] font-bold">
                        {(data?.kpis?.sessions?.change_pct || 0) >= 0 ? (
                            <span className="text-emerald-600 flex items-center">
                                <MdTrendingUp className="mr-0.5" /> +{data?.kpis?.sessions?.change_pct}%
                            </span>
                        ) : (
                            <span className="text-rose-600 flex items-center">
                                <MdTrendingDown className="mr-0.5" /> {data?.kpis?.sessions?.change_pct}%
                            </span>
                        )}
                    </div>
                </div>

                {/* Page Views */}
                <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <span className="text-[11px] font-semibold text-gray-400 block truncate">Page Views</span>
                    <div className="text-xl font-black text-purple-600 mt-1">
                        {fmt(data?.kpis?.page_views?.value)}
                    </div>
                    <div className="mt-1.5 flex items-center text-[10px] font-bold">
                        {(data?.kpis?.page_views?.change_pct || 0) >= 0 ? (
                            <span className="text-emerald-600 flex items-center">
                                <MdTrendingUp className="mr-0.5" /> +{data?.kpis?.page_views?.change_pct}%
                            </span>
                        ) : (
                            <span className="text-rose-600 flex items-center">
                                <MdTrendingDown className="mr-0.5" /> {data?.kpis?.page_views?.change_pct}%
                            </span>
                        )}
                    </div>
                </div>

                {/* Pages / Session */}
                <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <span className="text-[11px] font-semibold text-gray-400 block truncate">Pages / Session</span>
                    <div className="text-xl font-black text-teal-600 mt-1">
                        {data?.kpis?.pages_per_session?.value || 0}
                    </div>
                    <div className="mt-1.5 flex items-center text-[10px] font-bold">
                        {(data?.kpis?.pages_per_session?.change_pct || 0) >= 0 ? (
                            <span className="text-emerald-600 flex items-center">
                                <MdTrendingUp className="mr-0.5" /> +{data?.kpis?.pages_per_session?.change_pct}%
                            </span>
                        ) : (
                            <span className="text-rose-600 flex items-center">
                                <MdTrendingDown className="mr-0.5" /> {data?.kpis?.pages_per_session?.change_pct}%
                            </span>
                        )}
                    </div>
                </div>

                {/* Avg Duration */}
                <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <span className="text-[11px] font-semibold text-gray-400 block truncate">Avg Duration</span>
                    <div className="text-xl font-black text-emerald-600 mt-1">
                        {data?.kpis?.avg_session_duration?.formatted || "00:00"}
                    </div>
                    <div className="mt-1.5 flex items-center text-[10px] font-bold">
                        {(data?.kpis?.avg_session_duration?.change_pct || 0) >= 0 ? (
                            <span className="text-emerald-600 flex items-center">
                                <MdTrendingUp className="mr-0.5" /> +{data?.kpis?.avg_session_duration?.change_pct}%
                            </span>
                        ) : (
                            <span className="text-rose-600 flex items-center">
                                <MdTrendingDown className="mr-0.5" /> {data?.kpis?.avg_session_duration?.change_pct}%
                            </span>
                        )}
                    </div>
                </div>

                {/* Bounce Rate */}
                <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <span className="text-[11px] font-semibold text-gray-400 block truncate">Bounce Rate</span>
                    <div className="text-xl font-black text-amber-600 mt-1">
                        {data?.kpis?.bounce_rate?.value || 0}%
                    </div>
                    <div className="mt-1.5 flex items-center text-[10px] font-bold">
                        {(data?.kpis?.bounce_rate?.change_pct || 0) <= 0 ? (
                            <span className="text-emerald-600 flex items-center">
                                <MdTrendingDown className="mr-0.5" /> {data?.kpis?.bounce_rate?.change_pct}%
                            </span>
                        ) : (
                            <span className="text-rose-600 flex items-center">
                                <MdTrendingUp className="mr-0.5" /> +{data?.kpis?.bounce_rate?.change_pct}%
                            </span>
                        )}
                    </div>
                </div>

                {/* New Visitors */}
                <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <span className="text-[11px] font-semibold text-gray-400 block truncate">New Visitors</span>
                    <div className="text-xl font-black text-sky-600 mt-1">
                        {fmt(data?.kpis?.new_visitors?.value)}
                    </div>
                    <span className="text-[10px] text-gray-400 font-semibold block mt-1">
                        {data?.kpis?.new_visitors?.percentage}% of traffic
                    </span>
                </div>

                {/* Returning Visitors */}
                <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <span className="text-[11px] font-semibold text-gray-400 block truncate">Returning</span>
                    <div className="text-xl font-black text-violet-600 mt-1">
                        {fmt(data?.kpis?.returning_visitors?.value)}
                    </div>
                    <span className="text-[10px] text-gray-400 font-semibold block mt-1">
                        {data?.kpis?.returning_visitors?.percentage}% of traffic
                    </span>
                </div>
            </div>

            {/* 3. Interactive Traffic Time-Series Chart & Hourly Peak */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {/* Traffic Time-Series Chart (2 Columns) */}
                <div className="lg:col-span-2 bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex flex-col justify-between overflow-hidden">
                    <div>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                            <div>
                                <h3 className="font-bold text-gray-800 text-base">Visitor Traffic Time-Series</h3>
                                <p className="text-xs text-gray-500">Interactive trends across Unique Visitors, Sessions, and Page Views</p>
                            </div>

                            {/* Granularity Switcher */}
                            <div className="inline-flex bg-gray-100 p-1 rounded-lg text-xs font-semibold text-gray-700">
                                {['hourly', 'daily', 'weekly', 'monthly'].map((g) => (
                                    <button
                                        key={g}
                                        onClick={() => setGranularity(g)}
                                        className={`px-2.5 py-1 rounded-md capitalize transition-all ${
                                            granularity === g
                                                ? 'bg-white text-blue-600 shadow-sm font-bold'
                                                : 'text-gray-500 hover:text-gray-900'
                                        }`}
                                    >
                                        {g}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Interactive Legend (Click to Toggle Series) */}
                        <div className="flex flex-wrap items-center gap-4 text-xs font-semibold mb-4 bg-gray-50 p-2.5 rounded-xl border border-gray-100">
                            <span className="text-gray-400 text-[11px]">Legend (click to toggle):</span>
                            <button
                                onClick={() => setVisibleSeries(prev => ({ ...prev, unique_visitors: !prev.unique_visitors }))}
                                className={`flex items-center gap-1.5 px-2 py-0.5 rounded-md transition-all ${visibleSeries.unique_visitors ? 'text-blue-700 bg-blue-50 border border-blue-200' : 'text-gray-400 opacity-50'}`}
                            >
                                <span className="w-2.5 h-2.5 rounded-full bg-blue-600"></span>
                                Unique Visitors
                            </button>
                            <button
                                onClick={() => setVisibleSeries(prev => ({ ...prev, sessions: !prev.sessions }))}
                                className={`flex items-center gap-1.5 px-2 py-0.5 rounded-md transition-all ${visibleSeries.sessions ? 'text-indigo-700 bg-indigo-50 border border-indigo-200' : 'text-gray-400 opacity-50'}`}
                            >
                                <span className="w-2.5 h-2.5 rounded-full bg-indigo-500"></span>
                                Sessions
                            </button>
                            <button
                                onClick={() => setVisibleSeries(prev => ({ ...prev, page_views: !prev.page_views }))}
                                className={`flex items-center gap-1.5 px-2 py-0.5 rounded-md transition-all ${visibleSeries.page_views ? 'text-purple-700 bg-purple-50 border border-purple-200' : 'text-gray-400 opacity-50'}`}
                            >
                                <span className="w-2.5 h-2.5 rounded-full bg-purple-400"></span>
                                Page Views
                            </button>
                        </div>
                    </div>

                    {/* Chart Visualization Bars with safe scroll container */}
                    <div className="w-full overflow-x-auto pb-2 custom-scrollbar">
                        <div className="h-60 min-w-[500px] w-full flex items-end gap-1.5 sm:gap-2 pt-8 pb-2 border-b border-gray-100">
                            {chartPoints.length > 0 ? (
                                chartPoints.map((pt, i) => {
                                    const uvHeight = visibleSeries.unique_visitors ? Math.max(4, (pt.unique_visitors / maxChartVal) * 100) : 0;
                                    const sHeight = visibleSeries.sessions ? Math.max(4, (pt.sessions / maxChartVal) * 100) : 0;
                                    const pvHeight = visibleSeries.page_views ? Math.max(4, (pt.page_views / maxChartVal) * 100) : 0;

                                    return (
                                        <div key={i} className="flex-1 min-w-[18px] flex flex-col items-center h-full justify-end group relative">
                                            {/* Tooltip */}
                                            <div className="absolute -top-16 z-30 hidden group-hover:flex flex-col items-center bg-gray-900 text-white text-[10px] p-2 rounded-lg shadow-xl pointer-events-none whitespace-nowrap">
                                                <span className="font-bold border-b border-gray-700 pb-0.5 mb-1">{pt.label}</span>
                                                {visibleSeries.unique_visitors && <span>Unique: {fmt(pt.unique_visitors)}</span>}
                                                {visibleSeries.sessions && <span>Sessions: {fmt(pt.sessions)}</span>}
                                                {visibleSeries.page_views && <span>Views: {fmt(pt.page_views)}</span>}
                                            </div>

                                            <div className="w-full flex items-end justify-center gap-0.5 h-full">
                                                {visibleSeries.unique_visitors && (
                                                    <div style={{ height: `${uvHeight}%` }} className="w-1.5 sm:w-2.5 bg-blue-600 rounded-t-sm group-hover:bg-blue-700 transition-all"></div>
                                                )}
                                                {visibleSeries.sessions && (
                                                    <div style={{ height: `${sHeight}%` }} className="w-1.5 sm:w-2.5 bg-indigo-500 rounded-t-sm group-hover:bg-indigo-600 transition-all"></div>
                                                )}
                                                {visibleSeries.page_views && (
                                                    <div style={{ height: `${pvHeight}%` }} className="w-1.5 sm:w-2.5 bg-purple-400 rounded-t-sm group-hover:bg-purple-500 transition-all"></div>
                                                )}
                                            </div>
                                            <span className="text-[10px] text-gray-400 mt-2 truncate w-full text-center">
                                                {pt.label.split(' ')[0]}
                                            </span>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400 italic text-xs">
                                    No traffic points in this interval
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* 4. Visitor Statistics by Hour (24h Peak Heatmap) */}
                <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex flex-col justify-between">
                    <div>
                        <h3 className="font-bold text-gray-800 text-base flex items-center gap-2">
                            <MdSchedule className="text-blue-600" /> Hourly Peak Traffic
                        </h3>
                        <p className="text-xs text-gray-500 mb-4">Traffic distribution across 24 hours of the day</p>

                        <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
                            {data?.hourly_distribution?.map((h, idx) => {
                                const maxH = Math.max(...data.hourly_distribution.map(x => x.sessions), 1);
                                const pct = (h.sessions / maxH) * 100;
                                const isPeak = pct > 75;

                                return (
                                    <div key={idx} className="flex items-center gap-2 text-xs">
                                        <span className="font-mono text-[11px] text-gray-500 w-12">{h.hour}</span>
                                        <div className="flex-1 bg-gray-100 h-2.5 rounded-full overflow-hidden">
                                            <div
                                                style={{ width: `${pct}%` }}
                                                className={`h-full rounded-full transition-all ${isPeak ? 'bg-amber-500' : 'bg-blue-600'}`}
                                            ></div>
                                        </div>
                                        <span className={`text-[11px] w-8 text-right font-bold ${isPeak ? 'text-amber-600' : 'text-gray-700'}`}>
                                            {h.sessions}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className="text-[11px] text-gray-400 border-t pt-2 mt-2 flex items-center justify-between">
                        <span>Golden bars highlight peak readership hours</span>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs for Deep Dives */}
            <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 pb-3 mb-6">
                {[
                    { id: 'overview', label: 'Overview & Geography' },
                    { id: 'content', label: 'Top Pages & Articles' },
                    { id: 'sources', label: 'Traffic Sources & Campaigns' },
                    { id: 'tech', label: 'Devices, OS & Browsers' },
                    { id: 'realtime', label: 'Real-Time Activity' },
                ].map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                            activeTab === tab.id
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* TAB 1: OVERVIEW & GEOGRAPHY (Country, India State Drilldown, City) */}
            {activeTab === 'overview' && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* 5. Country Analytics */}
                        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                                <div>
                                    <h3 className="font-bold text-gray-800 text-base flex items-center gap-2">
                                        <MdPublic className="text-blue-600" /> Country Statistics
                                    </h3>
                                    <p className="text-xs text-gray-400">Click a country to filter (or click India for state drill-down)</p>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search country..."
                                    value={countrySearch}
                                    onChange={(e) => { setCountrySearch(e.target.value); setCountryPage(1); }}
                                    className="px-3 py-1 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none"
                                />
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs">
                                    <thead>
                                        <tr className="bg-gray-50 text-gray-600 border-b">
                                            <th className="py-2.5 px-3 font-bold">Country</th>
                                            <th className="py-2.5 px-3 font-bold text-right">Visitors</th>
                                            <th className="py-2.5 px-3 font-bold text-right">Sessions</th>
                                            <th className="py-2.5 px-3 font-bold text-right">Page Views</th>
                                            <th className="py-2.5 px-3 font-bold text-right">%</th>
                                            <th className="py-2.5 px-3 font-bold text-right">Avg Time</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {paginatedCountries.map((c, idx) => (
                                            <tr 
                                                key={idx} 
                                                onClick={() => {
                                                    setSelectedCountry(c.country);
                                                }}
                                                className={`hover:bg-blue-50/50 cursor-pointer transition-colors ${selectedCountry === c.country ? 'bg-blue-50 font-bold text-blue-700' : ''}`}
                                            >
                                                <td className="py-2.5 px-3 flex items-center gap-2">
                                                    <span>{c.country}</span>
                                                    {c.country === 'India' && (
                                                        <span className="text-[10px] bg-green-100 text-green-800 px-1.5 py-0.5 rounded font-bold">Drill down</span>
                                                    )}
                                                </td>
                                                <td className="py-2.5 px-3 text-right">{fmt(c.visitors)}</td>
                                                <td className="py-2.5 px-3 text-right">{fmt(c.sessions)}</td>
                                                <td className="py-2.5 px-3 text-right font-semibold">{fmt(c.page_views)}</td>
                                                <td className="py-2.5 px-3 text-right text-gray-500">{c.percentage}%</td>
                                                <td className="py-2.5 px-3 text-right font-mono text-[11px] text-gray-600">{c.avg_duration}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {filteredCountries.length > countryItemsPerPage && (
                                <div className="flex items-center justify-between mt-3 text-xs text-gray-500 border-t pt-2">
                                    <span>Page {countryPage} of {Math.ceil(filteredCountries.length / countryItemsPerPage)}</span>
                                    <div className="flex gap-1">
                                        <button
                                            disabled={countryPage === 1}
                                            onClick={() => setCountryPage(p => p - 1)}
                                            className="px-2.5 py-1 bg-gray-100 disabled:opacity-40 rounded"
                                        >
                                            Prev
                                        </button>
                                        <button
                                            disabled={countryPage * countryItemsPerPage >= filteredCountries.length}
                                            onClick={() => setCountryPage(p => p + 1)}
                                            className="px-2.5 py-1 bg-gray-100 disabled:opacity-40 rounded"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 6. India State-wise Statistics */}
                        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="font-bold text-gray-800 text-base flex items-center gap-2">
                                        <MdFlag className="text-orange-600" /> India State-wise Statistics
                                    </h3>
                                    <p className="text-xs text-gray-400">Click a state to filter down by that region</p>
                                </div>
                                <span className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full font-bold">
                                    {data?.india_states?.length || 0} States
                                </span>
                            </div>

                            <div className="space-y-2.5 max-h-[380px] overflow-y-auto pr-1">
                                {data?.india_states?.map((st, idx) => (
                                    <div 
                                        key={idx}
                                        onClick={() => setSelectedState(st.state)}
                                        className={`p-2.5 rounded-xl border border-gray-100 hover:bg-orange-50/50 cursor-pointer transition-colors ${selectedState === st.state ? 'bg-orange-50 border-orange-200 font-bold text-orange-900' : 'bg-gray-50'}`}
                                    >
                                        <div className="flex justify-between items-center text-xs mb-1">
                                            <span className="font-semibold text-gray-800">{st.state}</span>
                                            <div className="flex items-center gap-3">
                                                <span className="text-gray-500">{fmt(st.visitors)} visitors</span>
                                                <span className="font-bold text-blue-600">{fmt(st.page_views)} views</span>
                                                <span className="text-gray-400">({st.percentage}%)</span>
                                            </div>
                                        </div>
                                        <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden">
                                            <div 
                                                className="bg-orange-500 h-full rounded-full transition-all"
                                                style={{ width: `${st.percentage}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* 7. City-wise Statistics */}
                    <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                            <div>
                                <h3 className="font-bold text-gray-800 text-base flex items-center gap-2">
                                    <MdLocationCity className="text-indigo-600" /> City-wise Analytics
                                </h3>
                                <p className="text-xs text-gray-400">Top metro and district cities by engagement</p>
                            </div>
                            <input
                                type="text"
                                placeholder="Search city or state..."
                                value={citySearch}
                                onChange={(e) => { setCitySearch(e.target.value); setCityPage(1); }}
                                className="px-3 py-1 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none"
                            />
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs">
                                <thead>
                                    <tr className="bg-gray-50 text-gray-600 border-b">
                                        <th className="py-2.5 px-3 font-bold">City</th>
                                        <th className="py-2.5 px-3 font-bold">State</th>
                                        <th className="py-2.5 px-3 font-bold">Country</th>
                                        <th className="py-2.5 px-3 font-bold text-right">Visitors</th>
                                        <th className="py-2.5 px-3 font-bold text-right">Sessions</th>
                                        <th className="py-2.5 px-3 font-bold text-right">Page Views</th>
                                        <th className="py-2.5 px-3 font-bold text-right">Avg Duration</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {paginatedCities.map((ct, idx) => (
                                        <tr 
                                            key={idx}
                                            onClick={() => setSelectedCity(ct.city)}
                                            className={`hover:bg-indigo-50/50 cursor-pointer transition-colors ${selectedCity === ct.city ? 'bg-indigo-50 font-bold text-indigo-800' : ''}`}
                                        >
                                            <td className="py-2.5 px-3 font-semibold">{ct.city}</td>
                                            <td className="py-2.5 px-3 text-gray-600">{ct.state}</td>
                                            <td className="py-2.5 px-3 text-gray-500">{ct.country}</td>
                                            <td className="py-2.5 px-3 text-right">{fmt(ct.visitors)}</td>
                                            <td className="py-2.5 px-3 text-right">{fmt(ct.sessions)}</td>
                                            <td className="py-2.5 px-3 text-right font-semibold text-blue-600">{fmt(ct.page_views)}</td>
                                            <td className="py-2.5 px-3 text-right font-mono text-[11px] text-gray-500">{ct.avg_duration}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 2: CONTENT & TOP PAGES (Detailed Content Table + Trending Articles) */}
            {activeTab === 'content' && (
                <div className="space-y-6">
                    {/* Trending Articles Section */}
                    <div className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white p-5 rounded-2xl shadow-md">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                            <div>
                                <h3 className="font-bold text-base flex items-center gap-2">
                                    🔥 Trending Articles & Breaking News
                                </h3>
                                <p className="text-xs text-blue-200">Real-time velocity of articles over immediate timeframes</p>
                            </div>

                            <div className="inline-flex bg-white/10 p-1 rounded-xl text-xs font-semibold">
                                {[
                                    { id: 'last_1h', label: 'Last 1 Hour' },
                                    { id: 'last_6h', label: 'Last 6 Hours' },
                                    { id: 'last_24h', label: 'Last 24 Hours' },
                                ].map((btn) => (
                                    <button
                                        key={btn.id}
                                        onClick={() => setTrendingPeriod(btn.id)}
                                        className={`px-3 py-1 rounded-lg transition-all ${
                                            trendingPeriod === btn.id ? 'bg-white text-blue-900 font-bold' : 'text-blue-200 hover:text-white'
                                        }`}
                                    >
                                        {btn.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                            {data?.trending?.[trendingPeriod]?.map((tr, idx) => (
                                <div key={idx} className="bg-white/10 p-3 rounded-xl backdrop-blur-sm border border-white/10 flex flex-col justify-between">
                                    <span className="text-xs font-bold text-white line-clamp-2" title={tr.title}>
                                        {tr.title}
                                    </span>
                                    <div className="mt-3 flex items-center justify-between text-xs">
                                        <span className="text-blue-300 font-mono text-[11px]">{tr.path}</span>
                                        <span className="font-black text-amber-300 bg-amber-400/20 px-2 py-0.5 rounded">
                                            {tr.views} views
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Detailed Content Performance Table */}
                    <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                            <div className="flex items-center gap-4">
                                <h3 className="font-bold text-gray-800 text-base flex items-center gap-2">
                                    <MdArticle className="text-blue-600" /> All Pages & Articles Performance
                                </h3>
                                {/* Sorting Toggles */}
                                <div className="inline-flex bg-gray-100 p-0.5 rounded-lg text-xs font-semibold">
                                    <button 
                                        onClick={() => setContentSort('views')}
                                        className={`px-2.5 py-1 rounded-md ${contentSort === 'views' ? 'bg-white text-blue-600 font-bold shadow-sm' : 'text-gray-600'}`}
                                    >
                                        Most Viewed
                                    </button>
                                    <button 
                                        onClick={() => setContentSort('visitors')}
                                        className={`px-2.5 py-1 rounded-md ${contentSort === 'visitors' ? 'bg-white text-blue-600 font-bold shadow-sm' : 'text-gray-600'}`}
                                    >
                                        Most Visitors
                                    </button>
                                    <button 
                                        onClick={() => setContentSort('avg_time')}
                                        className={`px-2.5 py-1 rounded-md ${contentSort === 'avg_time' ? 'bg-white text-blue-600 font-bold shadow-sm' : 'text-gray-600'}`}
                                    >
                                        Avg Time
                                    </button>
                                </div>
                            </div>

                            <input
                                type="text"
                                placeholder="Search articles or URLs..."
                                value={pageSearch}
                                onChange={(e) => setPageSearch(e.target.value)}
                                className="px-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none w-full sm:w-64"
                            />
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs">
                                <thead>
                                    <tr className="bg-gray-50 text-gray-600 border-b">
                                        <th className="py-3 px-3 font-bold">Rank</th>
                                        <th className="py-3 px-3 font-bold">Page Title</th>
                                        <th className="py-3 px-3 font-bold">URL</th>
                                        <th className="py-3 px-3 font-bold text-right">Unique Visitors</th>
                                        <th className="py-3 px-3 font-bold text-right">Page Views</th>
                                        <th className="py-3 px-3 font-bold text-right">Avg Time</th>
                                        <th className="py-3 px-3 font-bold text-right">Entries</th>
                                        <th className="py-3 px-3 font-bold text-right">Exits</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredPages.map((pg, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                            <td className="py-3 px-3 text-gray-400 font-bold">#{pg.rank}</td>
                                            <td className="py-3 px-3 font-semibold text-gray-800 max-w-sm truncate" title={pg.title}>
                                                {pg.title}
                                            </td>
                                            <td className="py-3 px-3 font-mono text-[11px] text-gray-500 max-w-xs truncate">
                                                {pg.path}
                                            </td>
                                            <td className="py-3 px-3 text-right font-semibold text-blue-600">{fmt(pg.unique_visitors)}</td>
                                            <td className="py-3 px-3 text-right font-black text-gray-800">{fmt(pg.page_views)}</td>
                                            <td className="py-3 px-3 text-right font-mono text-gray-600">{pg.avg_time}</td>
                                            <td className="py-3 px-3 text-right text-gray-500">{fmt(pg.entry_count)}</td>
                                            <td className="py-3 px-3 text-right text-gray-500">{fmt(pg.exit_count)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 3: TRAFFIC SOURCES & CAMPAIGNS (Categories, Channels, UTM) */}
            {activeTab === 'sources' && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Traffic Categories */}
                        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                            <h3 className="font-bold text-gray-800 text-base mb-4">Traffic Categories</h3>
                            <div className="space-y-3">
                                {data?.traffic_categories?.map((tc, idx) => (
                                    <div 
                                        key={idx}
                                        onClick={() => setSelectedSource(tc.source)}
                                        className="p-3 bg-gray-50 rounded-xl border border-gray-100 hover:bg-blue-50/50 cursor-pointer transition-colors"
                                    >
                                        <div className="flex justify-between items-center text-xs mb-1.5">
                                            <span className="font-bold text-gray-800">{tc.source}</span>
                                            <div className="flex items-center gap-3">
                                                <span className="text-gray-500">{fmt(tc.sessions)} sessions</span>
                                                <span className="font-bold text-blue-600">{tc.percentage}%</span>
                                            </div>
                                        </div>
                                        <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                                            <div 
                                                className="bg-blue-600 h-full rounded-full transition-all"
                                                style={{ width: `${tc.percentage}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Specific Channels (Google, WhatsApp, etc.) */}
                        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                            <h3 className="font-bold text-gray-800 text-base mb-4">Specific Channels</h3>
                            <div className="space-y-2.5">
                                {data?.specific_sources?.map((ss, idx) => (
                                    <div 
                                        key={idx}
                                        onClick={() => setSelectedSource(ss.source)}
                                        className="flex items-center justify-between p-2.5 bg-gray-50 rounded-xl border border-gray-100 hover:bg-blue-50/50 cursor-pointer"
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <span className="text-base">{getSourceIcon(ss.source)}</span>
                                            <span className="text-xs font-semibold text-gray-800">{ss.source}</span>
                                        </div>
                                        <div className="flex items-center gap-4 text-xs">
                                            <span className="text-gray-500">{fmt(ss.visitors)} visitors</span>
                                            <span className="text-gray-600">{fmt(ss.sessions)} sessions</span>
                                            <span className="font-black text-blue-600">{ss.percentage}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* UTM Campaign Tracking Table */}
                    <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                        <h3 className="font-bold text-gray-800 text-base mb-2">UTM Campaigns & Marketing Links</h3>
                        <p className="text-xs text-gray-400 mb-4">Traffic originating from marketing tags (utm_source, utm_medium, utm_campaign)</p>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs">
                                <thead>
                                    <tr className="bg-gray-50 text-gray-600 border-b">
                                        <th className="py-2.5 px-3 font-bold">Campaign</th>
                                        <th className="py-2.5 px-3 font-bold">Source</th>
                                        <th className="py-2.5 px-3 font-bold">Medium</th>
                                        <th className="py-2.5 px-3 font-bold">Content</th>
                                        <th className="py-2.5 px-3 font-bold text-right">Visitors</th>
                                        <th className="py-2.5 px-3 font-bold text-right">Sessions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {data?.utm_campaigns && data.utm_campaigns.length > 0 ? (
                                        data.utm_campaigns.map((u, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50">
                                                <td className="py-2.5 px-3 font-bold text-blue-700">{u.utm_campaign}</td>
                                                <td className="py-2.5 px-3 font-semibold text-gray-700">{u.utm_source}</td>
                                                <td className="py-2.5 px-3 text-gray-500">{u.utm_medium}</td>
                                                <td className="py-2.5 px-3 text-gray-400">{u.utm_content}</td>
                                                <td className="py-2.5 px-3 text-right">{fmt(u.visitors)}</td>
                                                <td className="py-2.5 px-3 text-right font-bold text-gray-800">{fmt(u.sessions)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center py-6 text-gray-400 italic">
                                                No specific UTM tags recorded in this date range
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 4: DEVICES, OS & BROWSERS (Technology Breakdown) */}
            {activeTab === 'tech' && (
                <div className="space-y-6">
                    {/* Device Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {data?.devices_list?.map((d, idx) => (
                            <div 
                                key={idx}
                                onClick={() => setSelectedDevice(d.device)}
                                className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md cursor-pointer transition-shadow"
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        {d.device === 'Mobile' && <MdSmartphone className="text-blue-600 text-xl" />}
                                        {d.device === 'Desktop' && <MdComputer className="text-indigo-600 text-xl" />}
                                        {d.device === 'Tablet' && <MdTabletMac className="text-purple-600 text-xl" />}
                                        <h4 className="font-bold text-gray-800">{d.device}</h4>
                                    </div>
                                    <span className="text-lg font-black text-gray-800">{d.percentage}%</span>
                                </div>
                                <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden mb-3">
                                    <div 
                                        className="bg-blue-600 h-full rounded-full transition-all"
                                        style={{ width: `${d.percentage}%` }}
                                    ></div>
                                </div>
                                <div className="flex justify-between text-xs text-gray-500">
                                    <span>Visitors: <strong>{fmt(d.visitors)}</strong></span>
                                    <span>Views: <strong>{fmt(d.page_views)}</strong></span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Operating System & Browser Tables */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Operating Systems */}
                        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                            <h3 className="font-bold text-gray-800 text-base mb-4">Operating Systems</h3>
                            <div className="space-y-2.5">
                                {data?.operating_systems?.map((o, idx) => (
                                    <div 
                                        key={idx}
                                        onClick={() => setSelectedOS(o.os)}
                                        className="flex items-center justify-between p-2.5 bg-gray-50 rounded-xl hover:bg-blue-50/50 cursor-pointer"
                                    >
                                        <span className="text-xs font-semibold text-gray-800">{o.os}</span>
                                        <div className="flex items-center gap-3 text-xs">
                                            <span className="text-gray-500">{fmt(o.sessions)} sessions</span>
                                            <span className="font-bold text-blue-600">{o.percentage}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Browsers */}
                        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                            <h3 className="font-bold text-gray-800 text-base mb-4">Browsers</h3>
                            <div className="space-y-2.5">
                                {data?.browsers?.map((b, idx) => (
                                    <div 
                                        key={idx}
                                        onClick={() => setSelectedBrowser(b.browser)}
                                        className="flex items-center justify-between p-2.5 bg-gray-50 rounded-xl hover:bg-blue-50/50 cursor-pointer"
                                    >
                                        <span className="text-xs font-semibold text-gray-800">{b.browser}</span>
                                        <div className="flex items-center gap-3 text-xs">
                                            <span className="text-gray-500">{fmt(b.sessions)} sessions</span>
                                            <span className="font-bold text-purple-600">{b.percentage}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 5: REAL-TIME ACTIVITY (Live Pulse, Active Pages & Locations) */}
            {activeTab === 'realtime' && (
                <div className="space-y-6">
                    {/* Live Indicator Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-emerald-600 text-white p-5 rounded-2xl shadow-md">
                            <div className="flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-wider text-emerald-100">
                                <span className="relative flex h-3 w-3">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                                </span>
                                Currently Online
                            </div>
                            <div className="text-4xl font-black">
                                {data?.realtime?.currently_online || 0}
                            </div>
                            <span className="text-xs text-emerald-200 mt-1 block">Active visitors right now</span>
                        </div>

                        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                            <span className="text-xs font-semibold text-gray-400 uppercase">Last 5 Minutes</span>
                            <div className="text-3xl font-black text-gray-800 mt-2">
                                {data?.realtime?.last_5m || 0}
                            </div>
                            <span className="text-xs text-gray-500 mt-1 block">Recent active interactions</span>
                        </div>

                        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                            <span className="text-xs font-semibold text-gray-400 uppercase">Last 15 Minutes</span>
                            <div className="text-3xl font-black text-blue-600 mt-2">
                                {data?.realtime?.last_15m || 0}
                            </div>
                            <span className="text-xs text-gray-500 mt-1 block">Total sessions</span>
                        </div>

                        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                            <span className="text-xs font-semibold text-gray-400 uppercase">Last 30 Minutes</span>
                            <div className="text-3xl font-black text-indigo-600 mt-2">
                                {data?.realtime?.last_30m || 0}
                            </div>
                            <span className="text-xs text-gray-500 mt-1 block">Active readership</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Currently Active Pages */}
                        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                            <h3 className="font-bold text-gray-800 text-base mb-4">Pages Currently Being Read</h3>
                            <div className="space-y-3">
                                {data?.realtime?.active_pages?.map((ap, idx) => (
                                    <div key={idx} className="p-3 bg-gray-50 rounded-xl flex items-center justify-between">
                                        <div className="min-w-0 flex-1 pr-3">
                                            <p className="text-xs font-bold text-gray-800 truncate">{ap.title}</p>
                                            <p className="text-[11px] font-mono text-gray-400 truncate">{ap.path}</p>
                                        </div>
                                        <span className="text-xs font-black bg-blue-100 text-blue-800 px-2.5 py-1 rounded-lg">
                                            {ap.active} active
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Currently Active Geographies */}
                        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                            <h3 className="font-bold text-gray-800 text-base mb-4">Active Reader Locations</h3>
                            <div className="space-y-3">
                                {data?.realtime?.active_countries?.map((ac, idx) => (
                                    <div key={idx} className="p-3 bg-gray-50 rounded-xl flex items-center justify-between">
                                        <span className="text-xs font-bold text-gray-800">{ac.country}</span>
                                        <span className="text-xs font-black bg-emerald-100 text-emerald-800 px-2.5 py-1 rounded-lg">
                                            {ac.count} readers
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DetailedVisitorAnalytics;
